import { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface CategoryChannelsProps {
  providerId: number;
  versionId: number;
  categoryId: number;
  isReadOnly?: boolean;
}

interface ChannelNumber {
  id: number;
  type_id: number;
  number: string;
}

interface Channel {
  id: number;
  name: string;
  numbers: ChannelNumber[];
  is_default: boolean; 
}

type ColumnData = ColumnDef<Channel>;

export function CategoryChannels({ providerId, versionId, categoryId }: CategoryChannelsProps) {
  const { data: channelTypes = [] } = useQuery({
    queryKey: ['channelTypes', providerId, versionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('channel_types')
        .select('*')
        .eq('provider_id', providerId)
        .eq('version_id', versionId)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data || [];
    }
  });

  const { data: channelsData = [], isLoading, error } = useQuery({
    queryKey: ['category-channels', providerId, versionId, categoryId],
    queryFn: async () => {
      if (!categoryId) return [];
      
      // Get all channel_ids for this category
      const { data: categoryChannels, error: channelsError } = await supabase
        .from('channel_categories')
        .select('channel_id, is_default')
        .eq('category_id', categoryId);
      
      if (channelsError) throw channelsError;
      if (!categoryChannels?.length) return [];
      
      // Get channel details
      const channelIds = categoryChannels.map(cc => cc.channel_id);
      const { data: channels, error: detailsError } = await supabase
        .from('channels')
        .select(`
          id,
          name,
          numbers:channel_numbers(id, type_id, number)
        `)
        .in('id', channelIds);
      
      if (detailsError) throw detailsError;
      
      // Combine the data
      return (channels || []).map(channel => {
        const categoryChannel = categoryChannels.find(cc => cc.channel_id === channel.id);
        return {
          id: channel.id,
          name: channel.name,
          numbers: channel.numbers || [],
          is_default: categoryChannel?.is_default || false
        };
      });
    },
    enabled: !!categoryId
  });

  const columns = useMemo(() => {
    const cols: ColumnData[] = [
      {
        id: 'name',
        header: 'Channel',
        cell: ({ row }) => {
          const channel = row.original;
          return (
            <div className="flex items-center">
              <span>{channel.name}</span>
              {channel.is_default && (
                <Badge variant="outline" className="ml-2">Default</Badge>
              )}
            </div>
          );
        }
      }
    ];

    // Add columns for each channel type
    channelTypes.forEach(type => {
      cols.push({
        id: `number-${type.id}`,
        header: type.name,
        cell: ({ row }) => {
          const channel = row.original;
          const number = channel.numbers?.find(n => n.type_id === type.id);
          return number ? number.number : '-';
        }
      });
    });

    return cols;
  }, [channelTypes]);

  if (isLoading) return <Skeleton className="w-full h-32" />;

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load channels. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  if (!categoryId) {
    return (
      <Alert>
        <AlertDescription>
          Select a category to view its channels.
        </AlertDescription>
      </Alert>
    );
  }

  if (channelsData.length === 0) {
    return (
      <Alert>
        <AlertDescription>
          No channels found in this category.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column.id}>{column.header as React.ReactNode}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {channelsData.map((channel) => (
            <TableRow key={channel.id}>
              {columns.map((column) => (
                <TableCell key={column.id}>
                  {column.cell && typeof column.cell === 'function'
                    ? column.cell({ row: { original: channel } } as any)
                    : null
                  }
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
} 